import { useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchPropertyVendorsList } from 'entities/Vendors';
import { getRouteForbidden } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useInitialEffect } from 'shared/lib/hooks/useInitialEffect';
import { isArray } from 'shared/lib/lodash/lodash';

import { mapGlobalCategories } from './mapGlobalCategories';

import { fetchCategories } from '../model/services/fetchCategories/fetchCategories';
import { fetchGlobalCategories } from '../model/services/fetchGlobalCategories/fetchGlobalCategories';
import { categoryActions } from '../model/slices/CategorySlice';

export const useFetchCategories = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const propertyId = params?.propertyId;

    const initialCategoryIdParam = useRef(null);
    const initialLoaded = useRef(false);
    const navigateRef = useRef(null);

    useInitialEffect(() => {
        initialCategoryIdParam.current = params.categoryId;
        navigateRef.current = navigate;
    });

    const onFetchLocalCategories = useCallback(
        async (id: string, ignoreInitialCheck?: boolean) => {
            if (!id) {
                return;
            }

            const response = await dispatch(fetchCategories(id));
            await dispatch(fetchPropertyVendorsList(propertyId));

            if (ignoreInitialCheck) return;

            const categories = response.payload;
            const categoryId = initialCategoryIdParam.current;
            const initialized = initialLoaded.current;
            const navigate = navigateRef.current;

            if (categoryId && isArray(categories) && !initialized) {
                const foundCategory = categories.find(
                    (cat) => cat.id === categoryId,
                );

                if (foundCategory) {
                    dispatch(
                        categoryActions.selectCategory(
                            categories.find((cat) => cat.id === categoryId),
                        ),
                    );
                } else {
                    navigate(getRouteForbidden());
                }
                initialLoaded.current = true;
            }
        },
        [dispatch, propertyId],
    );

    const onFetchGlobalCategories = useCallback(async () => {
        const response = await dispatch(fetchGlobalCategories({ data: {} }));

        const categories = response.payload;
        const categoryId = initialCategoryIdParam.current;
        const initialized = initialLoaded.current;
        const navigate = navigateRef.current;

        if (categoryId && isArray(categories) && !initialized) {
            const convertedCategories = mapGlobalCategories(categories);

            const foundCategory = convertedCategories?.[categoryId];

            if (foundCategory) {
                dispatch(categoryActions.selectCategory(foundCategory));
            } else {
                navigate(getRouteForbidden());
            }
            initialLoaded.current = true;
        }
    }, [dispatch]);

    return { onFetchLocalCategories, onFetchGlobalCategories };
};
